import MAP_CONFIG from '../config/mapConfig';
import API_BASE_URL from '../config';

const memoryCache = new Map();
const STORAGE_KEY = 'sa_geo_cache_v2';
const CANADIAN_PROVINCES = new Set([
  'alberta',
  'british columbia',
  'manitoba',
  'new brunswick',
  'newfoundland and labrador',
  'nova scotia',
  'ontario',
  'prince edward island',
  'quebec',
  'saskatchewan',
  'northwest territories',
  'nunavut',
  'yukon',
]);

function normalize(value) {
  return (value || '').toString().trim().toLowerCase();
}

function keyFromTechnician(tech) {
  return [normalize(tech.address), normalize(tech.city), normalize(tech.province), normalize(tech.country)]
    .filter(Boolean)
    .join('|');
}

function loadPersistedCache() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const data = JSON.parse(raw);
    if (!data || typeof data !== 'object') return;
    Object.entries(data).forEach(([k, v]) => {
      if (Array.isArray(v) && v.length === 2) {
        memoryCache.set(k, { lat: Number(v[0]), lng: Number(v[1]) });
      }
    });
  } catch {
    // ignore malformed cache
  }
}

function persistCache() {
  try {
    const obj = {};
    memoryCache.forEach((coords, key) => {
      obj[key] = [coords.lat, coords.lng];
    });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch {
    // ignore storage issues
  }
}

loadPersistedCache();

async function geocodeWithNominatim(query) {
  const url = `${API_BASE_URL}/technician/geocode?q=${encodeURIComponent(query)}`;
  try {
    const res = await fetch(url, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data || !Number.isFinite(Number(data.lat)) || !Number.isFinite(Number(data.lng))) return null;
    return {
      lat: Number(data.lat),
      lng: Number(data.lng),
    };
  } catch {
    return null;
  }
}

async function geocodeQuery(query) {
  if (MAP_CONFIG.geocodingProvider === 'nominatim') {
    return geocodeWithNominatim(query);
  }
  return geocodeWithNominatim(query);
}

function resolveCountry(tech) {
  const province = normalize(tech.province);
  const country = normalize(tech.country);

  if (country.includes('canada')) return 'Canada';
  if (CANADIAN_PROVINCES.has(province)) return 'Canada';
  return tech.country;
}

function buildFallbackQueries(tech) {
  const country = resolveCountry(tech);
  const queries = [
    [tech.city, tech.province, country].filter(Boolean).join(', '),
    [tech.city, country].filter(Boolean).join(', '),
    [tech.province, country].filter(Boolean).join(', '),
    [tech.address, tech.city, tech.province, country].filter(Boolean).join(', '),
    [tech.address, tech.province, country].filter(Boolean).join(', '),
  ].filter(Boolean);

  return [...new Set(queries)];
}

function hasCoords(tech) {
  return Number.isFinite(tech.latitude) && Number.isFinite(tech.longitude);
}

export async function geocodeTechnicians(technicians = []) {
  const bounded = technicians.slice(0, MAP_CONFIG.maxGeocodePerBatch);
  const results = [];

  for (const tech of bounded) {
    if (hasCoords(tech)) {
      results.push({ ...tech, latitude: Number(tech.latitude), longitude: Number(tech.longitude) });
      continue;
    }

    const cacheKey = keyFromTechnician(tech);
    const cached = cacheKey ? memoryCache.get(cacheKey) : null;
    if (cached) {
      results.push({ ...tech, latitude: cached.lat, longitude: cached.lng });
      continue;
    }

    const candidateQueries = buildFallbackQueries(tech);
    if (candidateQueries.length === 0) continue;
    let coords = null;

    for (const candidate of candidateQueries) {
      coords = await geocodeQuery(candidate);
      if (coords && Number.isFinite(coords.lat) && Number.isFinite(coords.lng)) {
        break;
      }
    }

    if (coords && Number.isFinite(coords.lat) && Number.isFinite(coords.lng)) {
      if (cacheKey) memoryCache.set(cacheKey, coords);
      results.push({ ...tech, latitude: coords.lat, longitude: coords.lng });
      await new Promise((resolve) => setTimeout(resolve, MAP_CONFIG.geocodeDelayMs));
    }
  }

  persistCache();
  return results;
}
